// src/components/curated/EarlyAccessSheet.jsx
// "Book this trip" on curated trips: one-tap booking isn't live yet, so collect early access.
// Same copy, look and POST {LAMBDA}/early-access call as the EarlyAccessSheet in TripPlanner.js,
// plus the curated trip fields so the team can see which trip (and which version) they wanted.
import React, { useEffect, useState } from "react";
import { HOTEL_API } from "../../creator/creatorTripEngine";
import { useAuth } from "../../context/AuthContext";

const PURPLE = "#6B3FA0";
const ORANGE = "#F4560A";
const GREEN = "#00A86B";
const RED = "#E8334A";
const AMBER = "#F59E0B";

// Same identity TripPlanner uses (webbot_trip:<random8>)
const getUserId = () => {
    const KEY = "routechef_user_id";
    try {
        let id = localStorage.getItem(KEY);
        if (!id) {
            id = `webbot_trip:${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        return `webbot_trip:${Math.random().toString(36).slice(2, 10)}`;
    }
};

export default function EarlyAccessSheet({ dest, trip, onClose, onDone, onTalkToUs }) {
    // Signed-in users send their Firebase token, same as TripPlanner's getAuthHeaders(idToken)
    const { idToken } = useAuth() || {};
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | submitting | done | error
    const [errMsg, setErrMsg] = useState("");
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }, [onClose]);

    const handleSubmit = async () => {
        if (!isValid || status === "submitting") return;
        setStatus("submitting");
        setErrMsg("");
        try {
            const resp = await fetch(`${HOTEL_API}/early-access`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
                body: JSON.stringify({
                    email: email.trim(),
                    destination: dest || "",
                    user_id: getUserId(),
                    // extra context; the endpoint can ignore these
                    source: "curated_trip",
                    trip_slug: trip?.slug || "",
                    trip_title: trip?.title || "",
                    trip_origin: trip?.origin || "",
                    trip_start_date: trip?.start_date || "",
                    trip_pax: trip?.pax || "",
                    trip_total: trip?.total ? Math.round(trip.total) : "",
                    trip_summary: trip?.summary || "",
                }),
            });
            const data = await resp.json();
            if (data.success) {
                setStatus("done");
                onDone?.(email.trim());
            } else {
                setErrMsg(data.error || "Something went wrong. Please try again.");
                setStatus("error");
            }
        } catch {
            setErrMsg("Network error. Please try again.");
            setStatus("error");
        }
    };

    return (
        <div style={S.bg} className="ea-bg" onClick={onClose} data-leadbot-hide="">
            <div style={S.sheet} className="ea-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Get early access">
                <button onClick={onClose} style={S.close} aria-label="Close">×</button>
                {status === "done" ? (
                    <div style={{ textAlign: "center", padding: "12px 0 20px" }}>
                        <div style={{ fontSize: 48, marginBottom: 14 }}>🎉</div>
                        <div style={{ fontWeight: 800, fontSize: 18, color: "#111", marginBottom: 8 }}>You're on the list!</div>
                        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.65, maxWidth: 280, margin: "0 auto" }}>
                            We'll email you at <strong>{email}</strong> the moment one-click booking goes live.
                            <br />
                            We've saved this version of your {dest} trip too.
                        </div>
                        <button onClick={onClose} style={S.primaryRound}>Back to my trip</button>
                    </div>
                ) : (
                    <div>
                        <div style={S.hero}>
                            <div style={{ fontSize: 22, marginBottom: 6 }}>✈️🚂🏨</div>
                            <div style={{ fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 4 }}>
                                Book your entire {dest} trip in one tap
                            </div>
                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
                                Flights · trains · buses · hotels · all in one checkout. We're putting the finishing touches on it.
                            </div>
                        </div>

                        <div style={{ marginBottom: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                            {[
                                ["⚡", "Priority access before public launch"],
                                ["🎁", "Flat ₹200 off your first booking"],
                                ["📬", "Launch notification the moment it's live"],
                            ].map(([icon, text]) => (
                                <div key={text} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                                    <span style={{ fontSize: 13, color: "#444" }}>{text}</span>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <input
                                type="email"
                                autoFocus
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                                onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                                placeholder="your@email.com"
                                aria-label="Email address"
                                style={{
                                    ...S.input,
                                    border: `2px solid ${status === "error" ? RED : email && !isValid ? AMBER : isValid ? GREEN : "#E0E0E0"}`,
                                }}
                            />
                            {email && !isValid && <div style={{ fontSize: 11, color: AMBER, marginTop: 4, paddingLeft: 2 }}>Enter a valid email address</div>}
                            {errMsg && <div style={{ fontSize: 11, color: RED, marginTop: 4, paddingLeft: 2 }} role="alert">{errMsg}</div>}
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={!isValid || status === "submitting"}
                            style={{
                                ...S.submit,
                                background: isValid ? ORANGE : "#E0E0E0",
                                color: isValid ? "#fff" : "#aaa",
                                cursor: isValid ? "pointer" : "default",
                                boxShadow: isValid ? `0 4px 16px ${ORANGE}44` : "none",
                            }}
                        >
                            {status === "submitting" ? "Signing you up…" : "Get early access →"}
                        </button>

                        {onTalkToUs && (
                            <button onClick={onTalkToUs} style={S.talk}>
                                Want to book this now? Our trip team can do it for you →
                            </button>
                        )}
                        <div style={{ textAlign: "center", fontSize: 10, color: "#bbb", marginTop: 8 }}>No spam. Unsubscribe anytime. routechef.com</div>
                    </div>
                )}
            </div>
            <style>{`
@keyframes eaUp{from{transform:translateY(100%)}to{transform:none}}
.ea-sheet{animation:eaUp .24s cubic-bezier(.4,0,.2,1)}
@media (min-width: 640px){ .ea-bg{align-items:center !important} .ea-sheet{border-radius:20px !important} }
@media (prefers-reduced-motion: reduce){ .ea-sheet{animation:none} }
`}</style>
        </div>
    );
}

const S = {
    bg: { position: "fixed", inset: 0, zIndex: 960, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" },
    sheet: { position: "relative", background: "#fff", width: "100%", maxWidth: 440, borderRadius: "20px 20px 0 0", padding: "22px 18px calc(18px + env(safe-area-inset-bottom))", boxSizing: "border-box", maxHeight: "92vh", overflowY: "auto", fontFamily: "'Inter', -apple-system, system-ui, sans-serif" },
    close: { position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.9)", fontSize: 20, cursor: "pointer", color: "#333", zIndex: 1 },
    hero: { background: `linear-gradient(135deg,${PURPLE} 0%,#8B4DC8 60%,${ORANGE} 100%)`, borderRadius: 14, padding: "18px 44px 16px 18px", marginBottom: 20 },
    input: { width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 16, fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s", color: "#111", background: "#FAFAFA" },
    submit: { width: "100%", padding: "13px 0", border: "none", borderRadius: 11, fontFamily: "inherit", fontWeight: 700, fontSize: 14, transition: "all 0.15s" },
    talk: { width: "100%", marginTop: 10, border: "none", background: "transparent", color: PURPLE, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", padding: "6px 0" },
    primaryRound: { marginTop: 24, padding: "12px 36px", background: ORANGE, border: "none", borderRadius: 99, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14, color: "#fff", boxShadow: `0 4px 16px ${ORANGE}44` },
};
